<template>

    <Head :title="__('Login')" />

    <div class="flex items-center justify-center min-h-screen">
        <div class="w-full max-w-md border border-gray-100 bg-white rounded-md shadow-md p-8 mx-4">
            <div class="flex flex-col items-center mb-6">
                <Image :src="menuLogo"
                       class="h-12 mb-2" />
                <div class="text-2xl font-light text-gray-700">{{ __('Login') }}</div>
            </div>
            <v-form name="formLogin"
                    class="space-y-6"
                    @keyup.enter="login">
                <div>
                    <label for="email"
                           class="block text-gray-600 mb-1">{{ __('E-Mail Address') }}</label>
                    <InputText type="text"
                               id="email"
                               size="small"
                               style="width:100%"
                               :class="{ 'border-indigo-500': !form.errors.email, 'border-red-400': form.errors.email }"
                               class="rounded px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-indigo-200 w-full"
                               @input="form.errors.email = null"
                               v-model="form.email" />
                    <div v-if="form.errors.email"
                         class="text-red-500 text-sm mt-1">{{ __(form.errors.email) }}</div>
                    <div v-if="form.errors.timesToLock"
                         class="text-red-500 text-sm mt-1">{{ __(form.errors.timesToLock) }}</div>
                </div>
                <div>
                    <label for="password"
                           class="block text-gray-600 mb-1">{{ __('Password') }}</label>
                    <InputText type="password"
                               id="password"
                               size="small"
                               style="width:100%"
                               :class="{ 'border-indigo-500': !form.errors.password, 'border-red-400': form.errors.password }"
                               class="rounded px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-indigo-200 w-full"
                               @input="form.errors.password = null"
                               v-model="form.password" />
                    <div v-if="form.errors.password"
                         class="text-red-500 text-sm mt-1">{{ __(form.errors.password) }}</div>
                </div>
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <Checkbox inputId="remember"
                                  v-model="form.remember"
                                  binary />
                        <label for="remember"
                               class="ml-2 text-gray-600 text-sm">{{ __('Remember Me') }}</label>
                    </div>
                </div>
                <div class="flex items-center">
                    <Checkbox inputId="removeSession"
                              v-model="form.removeSession"
                              binary />
                    <label for="removeSession"
                           class="ml-2 text-gray-600 text-sm">{{ __('Remove session data') }}</label>
                </div>
                <div class="flex flex-col gap-2 mt-4">
                    <Button @click="login"
                            class="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition w-full"
                            label="Login"
                            icon="pi pi-sign-in" />
                    <Button v-if="isLoginViaGithub"
                            @click="githubLogin"
                            class="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-900 transition w-full"
                            label="Github"
                            icon="pi pi-github" />
                    <Button v-if="isLoginViaAzure"
                            @click="azureLogin"
                            class="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 transition w-full"
                            label="Azure"
                            icon="pi pi-microsoft" />
                </div>
                <div v-if="errorGa2FaLogin"
                     class="text-center text-red-500 text-base font-semibold mt-2">
                    {{ __('Invalid code specified in the second security level') }}
                </div>
                <div v-if="errorAzure"
                     class="text-center text-red-500 text-base font-semibold mt-2">
                    {{ errorAzure }}
                </div>
                <div class="flex items-center justify-center mt-4">
                    <i class="pi pi-star text-indigo-400 mr-2"
                       style="font-size: 1rem;"></i>
                    <Link :href="route('password-forget')"
                          class="text-indigo-600 hover:underline text-sm"
                          v-if="route().has('password-forget')">
                    {{ __('Forgot Your Password?') }}
                    </Link>
                </div>
            </v-form>
        </div>
    </div>

    <footer class="w-full flex items-center justify-between bg-gray-50 border-t border-gray-200 py-3 px-6 text-sm fixed bottom-0 left-0 z-10">
        <div class="flex items-center space-x-4">
            <Image :src="developerLogo"
                   class="h-8 w-auto" />
            <div>
                <span class="text-gray-500 font-light">Powered by © 4Evolve wersja 1.0</span>
            </div>
        </div>
    </footer>

</template>

<script setup>
import { useForm, usePage } from '@inertiajs/vue3'
import { Head } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3'

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Checkbox from 'primevue/checkbox';

import Layout from '@layouts/LayoutNoMenuNoFooter.vue'
import Image from '@helper/Image.vue'
import { __, setStoreValue, getStoreValue, getSessionValue, setSessionValue, getFromRoute } from '@helper/utils.js'
import { menuLogo, developerLogo, startSplash } from '@helper/constants.js'

defineOptions({ layout: Layout })

const props = defineProps({
    errorGa2FaLogin: Boolean,
    errorAzure: String,
    isLoginViaAzure: Boolean,
    isLoginViaGithub: Boolean,
})

// const isLoginViaGithub = false
// const isLoginViaAzure = true

// -----------------------------------------------------

const page = usePage();
const form = useForm({ email: '', password: '', remember: false, removeSession: false })

// -----------------------------------------------------

setStoreValue('appCaption', page.props.appCaption)
setStoreValue('systemRevision', page.props.systemRevision)

const isReloaded = getSessionValue("reloaded")
if (!isReloaded) {
    setSessionValue("reloaded", true)
}

// -----------------------------------------------------

const login = () => {
    form.post(route('app-login'))
    setSessionValue("reloaded", false)
}

// -----------------------------------------------------

const azureLogin = () => {
    window.location.href = '/azure/login';
    setSessionValue("reloaded", false)
}

// -----------------------------------------------------

const githubLogin = () => {
    window.location.href = '/github/login';
    setSessionValue("reloaded", false)
}

// -----------------------------------------------------

</script>

<style lang="scss">
body {
    background: url('/resources/images/textures/bg_body.png');
    background-position: center center;
    background-size: cover;
}
</style>
